import { useTheme } from '@fluentui/react';
import { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import useTimeFormatters from '../../shared/hooks/useTimeFormatters';
import Grid from '../shared/Grid';
import { FlexColumnDiv } from '../../shared/styled-components';
import { ITeamReport, MediaTypes } from '../../models/reports.internal';
import { defaultITeamKpiThresholds } from '../../models/reports.thresholds';
import { useGetTeamKpiThresholdsQuery } from '../../redux/apis/reportingThresholdsApi';
import { reduceWithSum } from '../../utils/arrayUtils';
import { objectKeys } from '../../utils/objectUtils';
import getTotalWorkItemCounts from '../../calculations/getTotalWorkItemCounts';
import palettes from '../../shared/colors';
import Card from '../shared/Card';
import TheUltimateBarChart, { ChartValue } from './TheUltimateBarChart';
import { AgentsAvailable, AgentsBusy, AgentsLoggedIn, AgentsOnBreak, TasksHandled, TasksMissed } from '../shared/icons';
import TeamKpiEditModal from './TeamKpiEditModal';
import ChartDisplayModal from './ChartDisplayModal';

const NoData = styled.div.attrs({ children: 'No data yet' })`
  text-align: center;
  font-style: italic;
  color: ${p => p.theme.palette.neutralSecondary};

  padding: 0.5rem 0;
`;

const getTopValues = (values: ChartValue[]): ChartValue[] => {
  if (values.length === 0)
    return [];

  if (values.length > 4)
    return [...values.slice(0, 3), { text: 'Other', value: reduceWithSum(values.slice(3), dc => dc.value), color: '' }];
  else
    return [...values];
};

interface IProps {
  expanded: boolean;
  isWideScreen: boolean;
  editEnabled: boolean;
  onEditClose(): void;
  teamReport: ITeamReport;
  sortedBreakReasons: ChartValue[];
  sortedDispositionCodes: ChartValue[];
}

const TeamOverviewCards: FC<IProps> = ({ expanded, isWideScreen, editEnabled, onEditClose, teamReport, sortedBreakReasons, sortedDispositionCodes }) => {
  const { palette } = useTheme();
  const { formatTimeSpan } = useTimeFormatters();

  const [breakReasonsOpen, setBreakReasonsOpen] = useState(false);
  const [tasksByDispositionOpen, setTasksByDispositionOpen] = useState(false);

  const { data: foundThresholds } = useGetTeamKpiThresholdsQuery(teamReport.id);
  const thresholds = useMemo(
    () => ({ ...defaultITeamKpiThresholds, ...foundThresholds }),
    [foundThresholds],
  );

  const loggedInCount = useMemo(
    () => {
      const numbers = objectKeys(teamReport.currentMemberAvailability)
        .filter(k => k !== 'LoggedOut')
        .map(k => teamReport.currentMemberAvailability[k]);

      return reduceWithSum(numbers);
    },
    [teamReport.currentMemberAvailability],
  );

  const { handled: totalHandled, missed: totalMissed } = useMemo(
    () => getTotalWorkItemCounts(teamReport.workItemsByMedia),
    [teamReport.workItemsByMedia],
  );

  const totalTimeLoggedIn = teamReport.timeSpentInStates.Busy + teamReport.timeSpentInStates.Break + teamReport.timeSpentInStates.Idle || 1;

  const getThresholdBarColor = useCallback(
    (value: number, threshold: number | null, behaviour: 'shouldBeLower' | 'shouldBeHigher'): string => {
      if (value === 0)
        return palette.neutralSecondary;

      if (!threshold)
        return palette.green;

      if (behaviour === 'shouldBeLower' && value <= threshold)
        return palette.green;

      if (behaviour === 'shouldBeHigher' && value >= threshold)
        return palette.green;

      return palette.red;
    },
    [palette],
  );

  const templateAreaRows = expanded
    ? isWideScreen
      ? [
        'agents '.repeat(8) + 'tasks '.repeat(4),
        'availability '.repeat(4) + 'breaks '.repeat(4) + 'disposition '.repeat(4),
        'voice '.repeat(3) + 'email '.repeat(3) + 'messaging '.repeat(3) + 'webchat '.repeat(3),
      ]
      : [
        'agents '.repeat(4) + 'tasks '.repeat(2),
        'availability '.repeat(3) + 'breaks '.repeat(3),
        'disposition '.repeat(6),
        'voice '.repeat(3) + 'email '.repeat(3),
        'messaging '.repeat(3) + 'webchat '.repeat(3),
      ]
    : ['agents '.repeat(2) + 'tasks '];

  return (<>
    <Grid rows={{ mode: 'auto', size: 'auto' }} columns={{ mode: 'equal' }} templateAreaRows={templateAreaRows} gap="0.5rem">
      <Card gridArea="agents"
        showSeparator
        minHeight={100}
        items={[
          { title: 'Agents Logged In', content: <AgentsLoggedIn text={loggedInCount} /> },
          { title: 'Agents Busy', content: <AgentsBusy text={teamReport.currentMemberAvailability.Busy} /> },
          { title: 'Agents On Break', content: <AgentsOnBreak text={teamReport.currentMemberAvailability.Break} /> },
          { title: 'Agents Available', content: <AgentsAvailable text={teamReport.currentMemberAvailability.Idle} /> },
        ]}
      />
      <Card gridArea="tasks"
        showSeparator
        minHeight={100}
        items={[
          { title: 'Tasks Handled', content: <TasksHandled text={totalHandled} /> },
          { title: 'Tasks Missed', content: <TasksMissed text={totalMissed} /> },
        ]}
      />
      {
        expanded &&
        <>
          <Card gridArea="availability"
            title="Team Utilization"
            items={{
              content: <TheUltimateBarChart
                values={[
                  { color: palette.red, text: 'Busy', value: teamReport.timeSpentInStates.Busy },
                  { color: palette.yellow, text: 'Break', value: teamReport.timeSpentInStates.Break },
                  { color: palette.green, text: 'Available', value: teamReport.timeSpentInStates.Idle },
                ]}
                valueFormatter={(val) => `${Math.round(val / totalTimeLoggedIn * 100)}% (${formatTimeSpan(val)})`}
              />,
            }}
          />
          <Card gridArea="breaks"
            title="Break Reasons"
            onExtrasButtonClick={() => setBreakReasonsOpen(true)}
            extrasButtonTooltip="View full list of break reasons"
            items={{
              content: sortedBreakReasons.length > 0
                ? <TheUltimateBarChart
                  values={getTopValues(sortedBreakReasons).map((v, i) => ({
                    color: palettes.yellow4[i],
                    text: v.text,
                    value: v.value,
                  }))}
                  valueFormatter={(val) => formatTimeSpan(val)}
                />
                : <NoData />,
            }}
          />
          <Card gridArea="disposition"
            title="Tasks by Disposition Code"
            onExtrasButtonClick={() => setTasksByDispositionOpen(true)}
            extrasButtonTooltip="View full list of tasks grouped by disposition codes"
            items={{
              content: sortedDispositionCodes.length > 0
                ? <TheUltimateBarChart
                  values={getTopValues(sortedDispositionCodes).map((v, i) => ({
                    color: palettes.blue4[i],
                    text: v.text,
                    value: v.value,
                  }))}
                />
                : <NoData />,
            }}
          />
          {
            MediaTypes.map(mt => {
              const handled = teamReport.workItemsByMedia[mt].handled;
              const totalTasksByMedia = teamReport.workItemsByMedia[mt].handled + teamReport.workItemsByMedia[mt].missed;
              const handledRatio = handled / (totalTasksByMedia || 1);

              return (
                <Card key={mt} gridArea={mt.toLowerCase()}
                  title={`${mt} tasks`}
                  minHeight={100}
                  items={{
                    content: <FlexColumnDiv gap="0.5rem">
                      <TheUltimateBarChart
                        values={[{
                          text: 'Average handling time',
                          value: teamReport.workItemsByMedia[mt].avgHandlingTime,
                          target: thresholds.averageHandlingTimeSeconds[mt] ?? undefined,
                          color: getThresholdBarColor(teamReport.workItemsByMedia[mt].avgHandlingTime, thresholds.averageHandlingTimeSeconds[mt], 'shouldBeLower'),
                        }]}
                        valueFormatter={(val) => formatTimeSpan(val)}
                      />
                      <TheUltimateBarChart
                        values={[{
                          text: 'Handled ratio',
                          value: handledRatio,
                          target: thresholds.handledRatio[mt] ?? undefined,
                          color: getThresholdBarColor(handledRatio, thresholds.handledRatio[mt], 'shouldBeHigher'),
                        }]}
                        maxValue={1}
                        valueFormatter={() => `${Math.round(handledRatio * 100)}% (${handled}/${totalTasksByMedia})`}
                      />
                    </FlexColumnDiv>,
                  }}
                />
              );
            })
          }
        </>
      }
    </Grid>
    <TeamKpiEditModal
      teamId={teamReport.id}
      isOpen={editEnabled}
      onDismiss={onEditClose}
    />
    <ChartDisplayModal title="Break Reasons"
      isOpen={breakReasonsOpen}
      onDismiss={() => setBreakReasonsOpen(false)}
      chartProps={{
        values: sortedBreakReasons.map(({ text, value }, i) => ({ text, value, color: palettes.yellow4[Math.min(i, palettes.yellow4.length - 1)] })),
        valueFormatter: (val) => formatTimeSpan(val),
      }}
    />
    <ChartDisplayModal title="Tasks by Disposition Code"
      isOpen={tasksByDispositionOpen}
      onDismiss={() => setTasksByDispositionOpen(false)}
      chartProps={{
        values: sortedDispositionCodes.map(({ text, value }, i) => ({ text, value, color: palettes.blue4[Math.min(i, palettes.blue4.length - 1)] })),
      }}
    />
  </>);
};

export default TeamOverviewCards;